/**
 * src/lib/api/client.ts
 * Glow API HTTP client. Uses HttpOnly cookies for auth (credentials: 'include').
 * No Authorization header. 401 triggers a token refresh then one retry.
 *
 * W17 security audit (MEDIUM, Finding #7): every mutating request also
 * carries X-Requested-With: XMLHttpRequest as CSRF defense-in-depth,
 * enforced gateway-side (glow-ai-api-gateway/app/main.py). This is on top
 * of, not instead of, the primary control — glow_access's SameSite cookie
 * attribute (auth-service, settings.COOKIE_SAMESITE, "lax" by default).
 * SameSite=Lax already blocks a cross-site page's fetch/XHR from sending
 * the cookie at all; this header is the second layer for the case where
 * an environment is ever configured with COOKIE_SAMESITE=none (needed if
 * the frontend and gateway end up on genuinely different registrable
 * domains) — a plain cross-site <form> POST can't set custom headers, so
 * it can never satisfy this check even without SameSite's help.
 */
const CSRF_HEADER = { 'X-Requested-With': 'XMLHttpRequest' } as const;

// Exported so callers that need an absolute URL outside the request()
// wrapper (e.g. EventSource, which has no fetch-style credentials option
// and must be pointed at a full/relative URL directly) can build one
// consistently rather than re-reading the env var themselves.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

if (import.meta.env.DEV && API_BASE_URL !== '') {
  console.warn(
    '[client] VITE_API_BASE_URL is set to:', API_BASE_URL,
    '— expected empty string for proxy-based dev. Check .env.development'
  )
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

let _redirecting = false;

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  retry = true,
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...CSRF_HEADER,
      ...(options.headers ?? {}),
    },
  });

  if (response.status === 401 && retry) {
    // Never attempt a refresh for auth endpoints that must be caller-handled.
    if (endpoint.includes('/auth/refresh') || endpoint.includes('/auth/login')) {
      throw new ApiError('Unauthorized', 401);
    }

    const refreshResponse = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: CSRF_HEADER,
    });

    if (refreshResponse.ok) {
      return request<T>(endpoint, options, false);
    }

    // /auth/me is the initial session probe — no valid refresh token means no
    // session yet. Return the 401 so AuthContext can set user=null and render
    // the login page without a hard redirect (which would loop on every load).
    if (endpoint.includes('/auth/me')) {
      throw new ApiError('Unauthorized', 401);
    }

    // Mid-session expiry on any other endpoint: redirect once.
    if (!_redirecting) {
      _redirecting = true;
      window.location.href = '/login';
    }
    throw new ApiError('Unauthorized', 401);
  }

  if (!response.ok) {
    // FastAPI HTTPException bodies carry the real message under `detail`
    // (e.g. "Workflow already claimed by alexis@glowhire.co.za") — without
    // reading it, every caller only ever saw the generic HTTP status text
    // ("Conflict", "Bad Request"), which is what left claim-lock 409s with
    // no way to show who actually has a booking claimed. Falls back to
    // statusText if the body isn't JSON or has no `detail` field.
    let message = response.statusText;
    try {
      const data = await response.json();
      if (data && typeof data.detail === 'string') {
        message = data.detail;
      }
    } catch {
      // body wasn't JSON (or already consumed) — keep statusText
    }
    throw new ApiError(message, response.status);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

export const get = <T>(endpoint: string): Promise<T> =>
  request<T>(endpoint);

export const post = <T>(endpoint: string, body?: unknown): Promise<T> =>
  request<T>(endpoint, { method: 'POST', body: JSON.stringify(body) });

/**
 * Best-effort fire-and-forget POST that the browser completes even while the
 * page is being closed or navigated away (`keepalive: true`). Used only for a
 * write that must not be lost on unload. No response handling and no 401
 * refresh/retry — there is no page left to act on either.
 */
export const postKeepalive = (endpoint: string, body?: unknown): void => {
  void fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...CSRF_HEADER },
    body: JSON.stringify(body),
  }).catch(() => {
    // page is going away; nothing useful to do with a failure
  });
};

export const put = <T>(endpoint: string, body?: unknown): Promise<T> =>
  request<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) });
export const patch = <T>(endpoint: string, body?: unknown): Promise<T> =>
  request<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body) });

export const del = <T>(endpoint: string, body?: unknown): Promise<T> =>
  request<T>(endpoint, {
    method: 'DELETE',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
